<script lang="ts">
type ColumnBase<TData extends RowData, TValue = unknown> = Omit<
	ColumnDefBase<TData, TValue>,
	"cell" | "getUniqueValue" | "footer"
>;

export interface ColumnProps<
	TData extends RowData,
	TKey extends DeepKeys<TData>,
	TValue extends DeepValue<TData, TKey>,
> extends ColumnBase<TData, TValue>,
		ClassProps {
	id?: string;
	field?: TKey & string;
	fieldFn?: AccessorFn<TData, TValue>;
	header?: string;
	hidden?: boolean;
	shrink?: boolean;
	style?: CSSProperties;
	csv?: boolean | ((ctx: CellContext<TData, TValue>) => unknown);
	/**
	 * Design this column as the primary column for mobile.
	 * This column will take 65% of the screen size.
	 */
	mobileMain?: boolean;
}

export type ColumnSlots<TData extends RowData, TValue = unknown> = {
	header?(args: HeaderContext<TData, TValue>): void;
	default?(args: CellContext<TData, TValue>): void;
};

export type AnyColumnProps = ColumnProps<any, any, any>;
</script>

<script
	setup
	lang="ts"
	generic="
		TData extends RowData,
		TKey extends DeepKeys<TData>,
		TValue extends DeepValue<TData, TKey>
	"
>
import type {
	AccessorFn,
	CellContext,
	ColumnDefBase,
	ColumnDefResolved,
	DeepKeys,
	DeepValue,
	HeaderContext,
	RowData,
} from "@tanstack/vue-table";
import { clsx } from "clsx";
import { isEqualWith } from "lodash-es";
import { useId } from "reka-ui";
import { onUnmounted, useSlots, watch, type CSSProperties } from "vue";
import type { ClassProps } from "../../lib/base-props";
import { injectColumnContext } from "./columns";

defineOptions({ inheritAttrs: false });

const props = defineProps<ColumnProps<TData, TKey, TValue>>();
defineSlots<ColumnSlots<TData, TValue>>();

const customId = useId(undefined, "column");

const slots = useSlots();

function createColumn(): ColumnDefResolved<TData, TValue> {
	const {
		field,
		fieldFn,
		hidden,
		shrink,
		meta,
		class: classes,
		style,
		mobileMain,
		csv,
		...rest
	} = props;
	const col: ColumnDefResolved<TData, TValue> = {
		accessorKey: field,
		accessorFn: fieldFn,
		...rest,
	};

	if (col.meta == null) {
		col.meta = {};
	}

	col.meta.hidden = hidden;
	col.meta.shrink = shrink;
	col.meta.class = classes;
	col.meta.style = style || {};
	col.meta.csv = csv;

	if (slots.default) {
		col.cell = slots.default;
	}

	if (slots.header) {
		col.header = slots.header as any;
	}

	if (mobileMain) {
		col.meta.class = clsx("md:min-w-auto min-w-[65vw] max-w-[65vw] md:max-w-none", col.meta.class);
	}

	return cleanUndefined(col) as ColumnDefResolved<TData, TValue>;
}

function cleanUndefined<T extends Record<string, any>>(obj: T): T {
	const cleaned = {} as T;
	for (const key in obj) {
		if (obj[key] !== undefined) {
			cleaned[key] = obj[key];
		}
	}

	return cleaned;
}

function propsChanged(props: AnyColumnProps, prevProps: AnyColumnProps): boolean {
	return !isEqualWith(props, prevProps, (value, other) => {
		// Function reference may change, but we don't want to trigger column update.
		// This may cause an infinite loop.
		if (typeof value === "function" && typeof other === "function") {
			return true;
		}

		return undefined;
	});
}

const columnsContext = injectColumnContext();

function updateColumn() {
	const column = createColumn();

	columnsContext.columns.set(customId, column as any);
}

watch(
	() => ({ ...props }),
	(props, prevProps) => {
		if (prevProps != null && !propsChanged(props, prevProps)) {
			return;
		}
		updateColumn();
	},
	{
		immediate: true,
		deep: true,
	}
);

onUnmounted(() => {
	columnsContext.columns.delete(customId);
});

if (import.meta.hot) {
	// Force full page reload when this component change on disk, since HMR don't preserve the column order.
	import.meta.hot.accept(() => {
		import.meta.hot?.invalidate();
	});

	// A bit dumb, since it will always be updated when we do HMR on the page.
	// It's not ideal, but it work for now and it's only in dev.
	import.meta.hot.on("vite:afterUpdate", updateColumn);
	onUnmounted(() => import.meta.hot?.off("vite:afterUpdate", updateColumn));
}
</script>

<template>{{ null }}</template>
